import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  FiAlertTriangle, FiCalendar, FiCheckCircle, FiClipboard, FiMessageSquare, FiPlus, FiShield, FiXCircle, FiCreditCard,
} from 'react-icons/fi';
import TimeClockCard from '../hr/TimeClockCard';
import type { HRBootstrap, PtoType } from '../hr/hrData';
import { daysUntil, usDate } from '../hr/licenseUtils';
import { createTimeOffRequest, getHRBootstrap, localDateKey } from '../../services/hrService';
import { listDwAgents, type DwAgentRecord } from '../claudeagents/claudeAgentApi';
import { useAgentUpdates, type AgentUpdate } from '../digitalworkforce/agentInbox';
import { colorForAgent, initialsForAgent, displayNameForAgent } from '../../utils/agentAvatar';
import { agentAvatarFor } from '../../utils/agentPhotos';
import { useActiveAgent } from '../../context/ActiveAgentContext';
import { fetchOpenPeopleCommands, COMMAND_STATUS_LABEL, type OpenCommand } from '../../services/agencyAdminService';
import { ROUTES } from '../../app/routes';
import '../../styles/HRCenter.css';
import '../../styles/MyDay.css';

/**
 * My Day — the time clock, time off, licenses, what's waiting on you, and
 * what your Digital Agents have been doing. HR pieces come from the same
 * HR Center API as /hr, so both pages always agree.
 */

const DIGITAL_AGENTS_KEY = 'ig-digital-team-members';

function greeting(d: Date) {
  const h = d.getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

type LicenseLevel = 'expired' | 'urgent' | 'soon' | 'upcoming' | 'ok';
function licenseLevel(days: number): LicenseLevel {
  if (days < 0) return 'expired';
  if (days <= 30) return 'urgent';
  if (days <= 60) return 'soon';
  if (days <= 90) return 'upcoming';
  return 'ok';
}
const LEVEL_TEXT: Record<LicenseLevel, (d: number) => string> = {
  expired: d => `Expired ${Math.abs(d)} day${Math.abs(d) === 1 ? '' : 's'} ago`,
  urgent: d => `Expires in ${d} day${d === 1 ? '' : 's'}`,
  soon: d => `Expires in ${d} days`,
  upcoming: d => `Expires in ${d} days`,
  ok: () => 'Active',
};

// ── Agents ─────────────────────────────────────────────────────────────────

const UPDATE_TEXT: Record<AgentUpdate['kind'], string> = {
  waiting: 'Needs you',
  review: 'Ready for review',
  'routine-done': 'Routine finished',
  'routine-failed': 'Routine failed',
};

const AgentActivity: React.FC<{ agent: DwAgentRecord }> = ({ agent }) => {
  const navigate = useNavigate();
  const { setActiveAgent } = useActiveAgent();
  const { updates, unread } = useAgentUpdates(agent);
  const name = displayNameForAgent(agent.name, agent.is_system);
  const photo = agentAvatarFor(agent.agent_id, agent.is_system ? name : undefined);
  const recent = [...updates].reverse().slice(0, 3);

  // Same as opening the agent from the Digital Team sidebar.
  const open = () => {
    setActiveAgent({ id: agent.agent_id, name, avatarColor: colorForAgent(agent.agent_id), avatarInitials: initialsForAgent(name), avatarPhoto: photo });
    navigate(ROUTES.DIGITAL_WORKFORCE, { state: { selectAgentId: agent.agent_id } });
  };

  return (
    <li className="myday-agent">
      <button type="button" className="myday-agent__head" onClick={open} aria-label={`Open ${name}`}>
        {photo
          ? <img src={photo} alt="" className="myday-agent__avatar" />
          : <span className="myday-agent__avatar" style={{ background: colorForAgent(agent.agent_id) }}>{initialsForAgent(name)}</span>}
        <span className="myday-agent__name">{name}</span>
        {unread.length > 0 && <span className="myday-badge">{unread.length} new</span>}
      </button>
      {recent.length === 0 ? <p className="myday-muted myday-agent__quiet">Nothing new in the last week.</p> : (
        <ul className="myday-agent__updates">
          {recent.map(u => (
            <li key={u.key}>
              <button
                type="button"
                className={`myday-update myday-update--${u.kind}`}
                onClick={() => (u.taskId ? navigate(`/momentum-kanban/${u.taskId}`) : open())}
              >
                <span className="myday-update__kind">{UPDATE_TEXT[u.kind]}</span>
                <span className="myday-update__title">{u.taskNumber ? `${u.taskNumber} · ` : ''}{u.title}</span>
                <span className="myday-update__when">{new Date(u.at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' })}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

function useMyAgents(): DwAgentRecord[] | null {
  const [agents, setAgents] = useState<DwAgentRecord[] | null>(null);
  useEffect(() => {
    listDwAgents().then(records => {
      const live = records.filter(r => !r.is_archived);
      let picked: string[] = [];
      try { picked = JSON.parse(localStorage.getItem(DIGITAL_AGENTS_KEY) || '[]'); } catch { /* none picked */ }
      const mine = picked.map(id => live.find(r => r.agent_id === id)).filter(Boolean) as DwAgentRecord[];
      setAgents(mine.length ? mine : live.filter(r => r.is_primary_agent));
    }).catch(() => setAgents([]));
  }, []);
  return agents;
}

// ── Page ───────────────────────────────────────────────────────────────────

const MyDayPage: React.FC = () => {
  const navigate = useNavigate();
  const agents = useMyAgents();
  const [hr, setHr] = useState<HRBootstrap | null>(null);
  const [hrError, setHrError] = useState<string | null>(null);
  const [commands, setCommands] = useState<OpenCommand[] | null>(null);
  const [showRequest, setShowRequest] = useState(false);
  const now = new Date();
  const today = localDateKey(now);
  const myUserId = (localStorage.getItem('userId') || '').trim();

  const loadHr = useCallback(async () => {
    try {
      setHr(await getHRBootstrap());
      setHrError(null);
    } catch (err) {
      setHrError(err instanceof Error ? err.message : 'Could not load your HR details.');
    }
  }, []);

  useEffect(() => { loadHr(); }, [loadHr]);

  useEffect(() => {
    fetchOpenPeopleCommands()
      .then(list => setCommands(list.filter(c => myUserId && c.personUserId === myUserId)))
      .catch(() => setCommands([]));
  }, [myUserId]);

  const me = hr?.me;
  const first = (localStorage.getItem('firstName') || '').trim() || me?.name.split(' ')[0] || '';
  const licenses = useMemo(() => (me?.licenses ?? [])
    .map(l => ({ l, days: daysUntil(l.expirationDate, now) }))
    .sort((a, b) => a.days - b.days),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [me?.licenses]);
  const myRequests = hr?.myRequests ?? [];
  const upcomingOff = myRequests.filter(r => r.status === 'approved' && r.endDate >= today).sort((a, b) => a.startDate.localeCompare(b.startDate));
  const holidays = (hr?.holidays ?? []).filter(h => h.date >= today).sort((a, b) => a.date.localeCompare(b.date)).slice(0, 2);
  const pendingForAdmin = hr?.isHrAdmin ? hr.pendingRequests.length : 0;
  const twoWeeksAgo = new Date(now.getTime() - 14 * 86400000).toISOString();

  // What needs attention, most urgent first.
  const alerts: Array<{ key: string; tone: 'bad' | 'warn' | 'good' | 'info'; icon: React.ReactNode; text: React.ReactNode; action?: () => void; actionLabel?: string }> = [];
  licenses.filter(x => licenseLevel(x.days) !== 'ok').forEach(({ l, days }) => {
    const level = licenseLevel(days);
    alerts.push({
      key: `lic-${l.id}`, tone: level === 'expired' || level === 'urgent' ? 'bad' : 'warn', icon: <FiCreditCard size={14} />,
      text: <><strong>{l.state} {l.linesOfAuthority} license</strong> — {LEVEL_TEXT[level](days)} ({usDate(l.expirationDate)}). {level === 'expired' ? "Don't sell or bind in this state until it's renewed." : 'Start your renewal.'}</>,
    });
  });
  myRequests.filter(r => (r.status === 'approved' || r.status === 'denied') && r.decidedAt && r.decidedAt >= twoWeeksAgo).forEach(r => {
    alerts.push({
      key: `to-${r.id}`, tone: r.status === 'approved' ? 'good' : 'info', icon: r.status === 'approved' ? <FiCheckCircle size={14} /> : <FiXCircle size={14} />,
      text: <>Your time off for {usDate(r.startDate)}{r.endDate !== r.startDate ? ` – ${usDate(r.endDate)}` : ''} was <strong>{r.status}</strong>.</>,
    });
  });
  if (pendingForAdmin > 0) alerts.push({ key: 'admin-pto', tone: 'info', icon: <FiShield size={14} />, text: <>{pendingForAdmin} time-off request{pendingForAdmin === 1 ? '' : 's'} waiting for your approval.</>, action: () => navigate(ROUTES.HR_CENTER), actionLabel: 'Review' });

  return (
    <div className="myday">
      <motion.div className="myday__inner" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
        <header className="myday__head">
          <h1 className="myday__title">{greeting(now)}{first ? `, ${first}` : ''}</h1>
          <p className="myday__date">{now.toLocaleDateString('en-US', { weekday: 'long' })}, {now.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}</p>
        </header>

        {hrError && (
          <div className="hr__alert" role="alert">
            <span>{hrError}</span>
            <button type="button" className="hr__alert-action" onClick={loadHr}>Retry</button>
          </div>
        )}

        <div className="myday__grid">
          <div className="myday__col">
            <section aria-label="Time clock">
              {me ? <TimeClockCard me={me} /> : <p className="myday-muted">{hrError ? 'Time clock unavailable.' : 'Loading…'}</p>}
            </section>

            <section className="myday-card glass-surface">
              <h2 className="myday-card__title"><FiMessageSquare size={14} aria-hidden="true" />Your Digital Agents</h2>
              {agents === null ? <p className="myday-muted">Loading…</p>
                : agents.length === 0 ? <p className="myday-muted">No Digital Agents yet. Add them from Digital Agents in the sidebar.</p>
                : <ul className="myday-agents">{agents.map(a => <AgentActivity key={a.agent_id} agent={a} />)}</ul>}
            </section>

            <section className="myday-card glass-surface">
              <h2 className="myday-card__title"><FiClipboard size={14} aria-hidden="true" />Your open commands</h2>
              {commands === null ? <p className="myday-muted">Loading…</p>
                : commands.length === 0 ? <p className="myday-muted">Nothing assigned to you right now.</p>
                : (
                  <ul className="myday-list">
                    {commands.slice(0, 6).map(c => (
                      <li key={c.id}>
                        <button type="button" className="myday-row" onClick={() => navigate(`/momentum-kanban/${c.id}`)}>
                          <span className="myday-row__code">{c.code}</span>
                          <span className="myday-row__title">{c.title || 'Untitled command'}</span>
                          <span className={`myday-status myday-status--${c.status}`}>{COMMAND_STATUS_LABEL[c.status]}</span>
                        </button>
                      </li>
                    ))}
                    {commands.length > 6 && (
                      <li><button type="button" className="myday-link" onClick={() => navigate(ROUTES.MOMENTUM_COMMAND_DETAIL.replace('/:taskId?', ''))}>See all {commands.length} in Command Center</button></li>
                    )}
                  </ul>
                )}
            </section>
          </div>

          <div className="myday__col">
            <section className="myday-card glass-surface myday-card--attention" aria-label="Needs your attention">
              <h2 className="myday-card__title"><FiAlertTriangle size={14} aria-hidden="true" />Needs your attention</h2>
              {alerts.length === 0 ? <p className="myday-muted myday-allclear"><FiCheckCircle size={14} aria-hidden="true" />You&apos;re all caught up.</p> : (
                <ul className="myday-alerts">
                  {alerts.map(a => (
                    <li key={a.key} className={`myday-alert myday-alert--${a.tone}`}>
                      <span className="myday-alert__icon" aria-hidden="true">{a.icon}</span>
                      <span className="myday-alert__text">{a.text}</span>
                      {a.action && <button type="button" className="myday-btn myday-btn--sm" onClick={a.action}>{a.actionLabel}</button>}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {me && hr && (
              <section className="myday-card glass-surface">
                <div className="myday-card__row">
                  <h2 className="myday-card__title"><FiCalendar size={14} aria-hidden="true" />Time off</h2>
                  {!showRequest && <button type="button" className="myday-btn myday-btn--primary myday-btn--sm" onClick={() => setShowRequest(true)}><FiPlus size={13} aria-hidden="true" />Request time off</button>}
                </div>
                <div className="myday-pto">
                  <span className="myday-pto__hours">{me.ptoBalanceHours}<small>h</small></span>
                  <span className="myday-pto__label">PTO available<br /><span className="myday-muted">about {(me.ptoBalanceHours / 8).toFixed(1)} days</span></span>
                </div>
                {showRequest && (
                  <TimeOffForm
                    ptoTypes={hr.ptoTypes}
                    balance={me.ptoBalanceHours}
                    onClose={() => setShowRequest(false)}
                    onSent={loadHr}
                  />
                )}
                {myRequests.filter(r => r.status === 'pending').map(r => (
                  <p key={r.id} className="myday-pending">Pending: {r.hours}h on {usDate(r.startDate)}{r.endDate !== r.startDate ? ` – ${usDate(r.endDate)}` : ''}</p>
                ))}
                {(upcomingOff.length > 0 || holidays.length > 0) && (
                  <ul className="myday-list myday-upcoming">
                    {upcomingOff.map(r => <li key={r.id}><span>{usDate(r.startDate)}</span><span>Your time off · {r.hours}h</span></li>)}
                    {holidays.map(h => <li key={h.id}><span>{usDate(h.date)}</span><span>{h.name} (paid holiday)</span></li>)}
                  </ul>
                )}
              </section>
            )}

            {me && hr && (
              <section className="myday-card glass-surface">
                <h2 className="myday-card__title"><FiCreditCard size={14} aria-hidden="true" />Your licenses</h2>
                {licenses.length === 0 ? (
                  <p className="myday-muted">
                    No licenses on file. {hr.isHrAdmin ? 'Add them in HR Center → Directory' : 'Ask your admin to add them'} so you get renewal reminders 90, 60 and 30 days out.
                  </p>
                ) : (
                  <ul className="myday-list">
                    {licenses.map(({ l, days }) => {
                      const level = licenseLevel(days);
                      return (
                        <li key={l.id} className="myday-license">
                          <span className="myday-license__state">{l.state}</span>
                          <span className="myday-license__who">
                            <span>{l.linesOfAuthority}</span>
                            <span className="myday-muted">{l.licenseNumber ? `#${l.licenseNumber} · ` : ''}exp {usDate(l.expirationDate)}</span>
                          </span>
                          <span className={`myday-license__status myday-license__status--${level}`}>{LEVEL_TEXT[level](days)}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ── Forms ──────────────────────────────────────────────────────────────────

const TimeOffForm: React.FC<{ ptoTypes: PtoType[]; balance: number; onClose: () => void; onSent: () => void }> = ({ ptoTypes, balance, onClose, onSent }) => {
  const [typeId, setTypeId] = useState(ptoTypes[0]?.id ?? 0);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [hours, setHours] = useState('8');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valid = typeId && start && (!end || end >= start) && Number(hours) > 0;
  const deducts = ptoTypes.find(t => t.id === typeId)?.deductsBalance ?? true;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      await createTimeOffRequest({ ptoTypeId: typeId, startDate: start, endDate: end || start, hours: Number(hours), comments: note.trim() });
      onSent();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send your request.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="myday-form" onSubmit={submit}>
      <label>Type<select value={typeId} onChange={e => setTypeId(Number(e.target.value))}>{ptoTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select></label>
      <label>First day<input type="date" value={start} onChange={e => setStart(e.target.value)} required /></label>
      <label>Last day<input type="date" value={end} min={start} onChange={e => setEnd(e.target.value)} /></label>
      <label>Hours<input type="number" min="0.5" step="0.5" value={hours} onChange={e => setHours(e.target.value)} /></label>
      <label className="myday-form__wide">Note for your manager<input value={note} onChange={e => setNote(e.target.value)} placeholder="Optional" /></label>
      {deducts && Number(hours) > balance && <p className="myday-form__warn myday-form__wide">That&apos;s more than your {balance}h balance. Your manager can still approve it.</p>}
      {error && <p className="myday-form__warn myday-form__wide">{error}</p>}
      <div className="myday-form__actions myday-form__wide">
        <button type="button" className="myday-btn" onClick={onClose}>Cancel</button>
        <button type="submit" className="myday-btn myday-btn--primary" disabled={!valid || busy}>Send request</button>
      </div>
    </form>
  );
};

export default MyDayPage;
